/**
 * IntakeForm — first-touch form rendered inside the ChatWidget panel when the
 * current session has no messages yet.
 *
 *  - Site URL is optional — picker is a dropdown sourced from redux
 *    `state.sitesInfo.allSites`. If empty we dispatch API_ALL_SITES once.
 *  - "Issue category" classifies the ticket into site / organization /
 *    payment buckets so operators can route faster (Power Apps reads
 *    `cr0b4_category`).
 *  - `user_urgency` keeps the spec's vocabulary on the wire
 *    (`critical|important|when_you_can`); the "informational" label is just
 *    friendlier copy for the lowest tier.
 *  - Per spec, the submitted `site_url` is normalized (scheme/path stripped).
 *  - Palette matches the host app (--primary-main #135fc2).
 */
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Actions } from "../../redux/constants/actionTypes";

const URGENCIES = [
  { value: "critical", label: "Critical — outage" },
  { value: "important", label: "Important — partial impact" },
  { value: "when_you_can", label: "Informational — low priority" },
];

const CATEGORIES = [
  { value: "site_issue", label: "Site Details" },
  { value: "organization_issue", label: "Organization Details" },
  { value: "payment_issue", label: "Plan / Payment Details" },
];

const ISSUE_SUMMARY_MAX = 500;
const ERROR_TEXT_MAX = 2000;

/**
 * Pull user-visible site identifiers out of `state.sitesInfo.allSites`,
 * filtered to the selected org when the shape carries multiple buckets.
 * We try a generous set of field names so different API shapes still work.
 */
function extractSiteNames(allSites, selectedOrgId) {
  if (!allSites) return [];

  const pickName = (s) =>
    s?.siteName ||
    s?.domainName ||
    s?.name ||
    s?.siteUrl ||
    s?.domain ||
    s?.url ||
    null;

  // Shape A: { siteDetailsResponses: [...] }  — already org-scoped.
  if (!Array.isArray(allSites) && Array.isArray(allSites.siteDetailsResponses)) {
    return allSites.siteDetailsResponses.map(pickName).filter(Boolean);
  }

  // Shape B: [{ orgId, siteDetailsResponses }, ...]
  if (Array.isArray(allSites)) {
    const buckets =
      selectedOrgId != null && selectedOrgId !== -1
        ? allSites.filter((b) => b?.orgId === selectedOrgId)
        : allSites;
    const names = [];
    buckets.forEach((bucket) => {
      (bucket?.siteDetailsResponses || []).forEach((s) => {
        const n = pickName(s);
        if (n) names.push(n);
      });
    });
    return names;
  }

  // Shape C: { someKey: [...] } — be tolerant. Walk own values; flat-map
  // anything that looks like a site array.
  if (!Array.isArray(allSites) && typeof allSites === "object") {
    const names = [];
    Object.values(allSites).forEach((v) => {
      if (Array.isArray(v)) v.forEach((s) => {
        const n = pickName(s);
        if (n) names.push(n);
      });
    });
    return names;
  }

  return [];
}

/** Normalize per spec: strip scheme + trailing slash; accept slugs as-is. */
function normalizeSiteUrl(raw) {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";
  const candidate = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const u = new URL(candidate);
    if (u.hostname) return u.hostname.replace(/\/+$/, "");
  } catch (_e) {
    /* not a URL — fall through to slug */
  }
  return trimmed.replace(/^https?:\/\//i, "").replace(/\/+$/, "");
}

export default function IntakeForm({ onSubmit, submitting }) {
  const dispatch = useDispatch();
  const allSites = useSelector((state) => state.sitesInfo?.allSites);
  const selectedOrgId = useSelector(
    (state) => state.organizationsInfo?.selectedOrg?.orgId,
  );

  const siteOptions = useMemo(
    () => extractSiteNames(allSites, selectedOrgId),
    [allSites, selectedOrgId],
  );

  const hasSites = siteOptions.length > 0;
  const [requestedFetch, setRequestedFetch] = useState(false);

  // If sites aren't in redux yet, dispatch the existing API_ALL_SITES saga
  // exactly once. Works for both org mode and individual mode (orgId === -1).
  useEffect(() => {
    if (hasSites || requestedFetch || selectedOrgId == null) return;
    setRequestedFetch(true);
    dispatch({
      type: Actions.API_ALL_SITES,
      data: { orgId: selectedOrgId },
      meta: { onSuccess: () => {}, onError: () => {} },
    });
  }, [hasSites, requestedFetch, selectedOrgId, dispatch]);

  // Site URL is OPTIONAL — empty string = "no site". User can still pick one.
  const [siteSelection, setSiteSelection] = useState("");

  const [category, setCategory] = useState(CATEGORIES[0].value);
  const [issueSummary, setIssueSummary] = useState("");
  const [userUrgency, setUserUrgency] = useState("important");
  const [errorText, setErrorText] = useState("");

  const canSubmit =
    !submitting &&
    issueSummary.trim().length > 0 &&
    issueSummary.trim().length <= ISSUE_SUMMARY_MAX &&
    errorText.length <= ERROR_TEXT_MAX;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    const trimmedError = errorText.trim();
    const normalizedSite = normalizeSiteUrl(siteSelection);
    onSubmit({
      siteUrl: normalizedSite || undefined,
      category,
      issueSummary: issueSummary.trim(),
      userUrgency,
      errorText: trimmedError.length > 0 ? trimmedError : undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} style={styles.wrap}>
      <div style={styles.title}>Get help with your Rocon site</div>

      <label style={styles.label}>Site URL (optional)</label>
      <select
        value={siteSelection}
        onChange={(e) => setSiteSelection(e.target.value)}
        style={styles.input}
        disabled={submitting}
      >
        <option value="">
          {hasSites ? "— Pick a site (optional) —"
            : requestedFetch ? "Loading your sites…" : "No site selected"}
        </option>
        {siteOptions.map((name) => (
          <option key={name} value={name}>
            {name}
          </option>
        ))}
      </select>

      <label style={styles.label}>What's this about? *</label>
      <div style={styles.radioGroup}>
        {CATEGORIES.map((c) => (
          <label key={c.value} style={styles.radioRow}>
            <input
              type="radio"
              name="rocon-chat-category"
              value={c.value}
              checked={category === c.value}
              onChange={() => setCategory(c.value)}
              disabled={submitting}
            />
            <span>{c.label}</span>
          </label>
        ))}
      </div>

      <label style={styles.label}>How can we help? *</label>
      <textarea
        value={issueSummary}
        onChange={(e) => setIssueSummary(e.target.value.slice(0, ISSUE_SUMMARY_MAX))}
        placeholder="My site is showing 502 errors and SSL is stuck on pending since today."
        rows={4}
        maxLength={ISSUE_SUMMARY_MAX}
        style={{ ...styles.input, resize: "vertical", minHeight: 72 }}
        disabled={submitting}
      />
      <div style={styles.counter}>
        {issueSummary.length} / {ISSUE_SUMMARY_MAX}
      </div>

      <label style={styles.label}>How urgent is this? *</label>
      <div style={styles.radioGroup}>
        {URGENCIES.map((u) => (
          <label key={u.value} style={styles.radioRow}>
            <input
              type="radio"
              name="rocon-chat-urgency"
              value={u.value}
              checked={userUrgency === u.value}
              onChange={() => setUserUrgency(u.value)}
              disabled={submitting}
            />
            <span>{u.label}</span>
          </label>
        ))}
      </div>

      <label style={styles.label}>Error message you're seeing (optional)</label>
      <input
        type="text"
        value={errorText}
        onChange={(e) => setErrorText(e.target.value.slice(0, ERROR_TEXT_MAX))}
        placeholder="502 Bad Gateway"
        maxLength={ERROR_TEXT_MAX}
        style={styles.input}
        disabled={submitting}
      />

      {/* TODO: screenshot upload + server-side OCR */}

      <button
        type="submit"
        disabled={!canSubmit}
        style={{
          ...styles.submitBtn,
          opacity: canSubmit ? 1 : 0.6,
          cursor: canSubmit ? "pointer" : "not-allowed",
        }}
      >
        {submitting ? "Starting…" : "Start chat"}
      </button>
    </form>
  );
}

const styles = {
  wrap: {
    flex: 1,
    overflowY: "auto",
    padding: 14,
    background: "#fbfcfe",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  title: {
    fontSize: 15,
    fontWeight: 600,
    color: "#0b0d0e",
    marginBottom: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 600,
    color: "#445063",
    marginTop: 6,
  },
  input: {
    padding: "8px 10px",
    borderRadius: 8,
    border: "1px solid #d2dff1",
    fontSize: 14,
    outline: "none",
    background: "white",
    fontFamily: "inherit",
    color: "#171a1c",
  },
  counter: {
    alignSelf: "flex-end",
    fontSize: 11,
    color: "#677488",
    marginTop: -4,
  },
  radioGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    padding: "4px 2px",
  },
  radioRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 13,
    color: "#171a1c",
    cursor: "pointer",
  },
  submitBtn: {
    marginTop: 10,
    background: "#135fc2",
    color: "white",
    border: "none",
    padding: "10px 14px",
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
  },
};
